// Tokens for the "NEW / Event Dashboard / From image brief" screen (Figma 3324:18828, page
// "PAVEL DRAFT"). The brief is drawn on the 390-wide mobile frame with a 360-wide content
// column, so every number in this folder is in that design space and scaled once by the screen.
export const EVENT_FONT = 'Sofia Sans';
export const EVENT_FONT_CONDENSED = 'Sofia Sans Semi Condensed';

export const DESIGN_FRAME_WIDTH = 390;
export const DESIGN_CONTENT_WIDTH = 360;
/** The design's own panels inset their contents by 8 a side. */
export const DESIGN_PANEL_PADDING = 8;
/** Vertical rhythm between the panels on the brief (hero -> points -> tabs -> tracks -> ...). */
export const DESIGN_SECTION_GAP = 12;
/** Wider viewports centre the column rather than stretching it, same cap as the home redesign. */
export const MAX_SCALE = 1.15;

// The named entries below are the file's own variables, read off the brief with
// get_variable_defs: text/primary, text/accent, gold/50..gold/700, Button/Green/Stroke and
// var(--glow-bloom-70). The rest (plate, well, tracks) are gradients in Figma, which that API
// returns unresolved - those keep the values the rest of the app already paints them with.
export const eventColors = {
	panelBorder: '#DA600E',
	panelBorderGold: '#FFF908',
	panelBorderBright: '#FCF17B',
	tileBorder: 'rgba(255,140,31,0.35)',
	countdownBorder: '#E08712',
	countdownPlate: 'rgba(23,4,0,0.9)',
	passButtonBorder: '#8C6B24',
	surface: '#330E07',
	well: '#1A0603',
	white: '#FFFFFF',
	gold: '#FFD766',
	goldStrong: '#F5A300',
	goldDeep: '#CE7C00',
	goldSoft: '#FFE9A8',
	goldPale: '#FFF5B9',
	accent: '#FFFF99',
	label: '#FFF5EB',
	/** The brief's muted olive - milestone track, locked labels, the pass placeholder outline. */
	muted: '#9A9A62',
	progressTrack: '#330B05',
	lockedNode: '#2A1511',
	green: '#0CEB66',
	greenStroke: 'rgba(184,255,140,0.75)',
} as const;

/** Vertical gradients are painted with an SVG rect - the app has no gradient view library. */
export const PANEL_FILL: Array<[string, string]> = [
	['0', '#571405'],
	['1', '#1F0503'],
];
export const TILE_FILL: Array<[string, string]> = [
	['0.4312', '#571405'],
	['0.99912', '#1F0503'],
];
export const TILE_CLAIMABLE_FILL: Array<[string, string]> = [
	['0', '#770700'],
	['0.801', '#320400'],
	['1', '#320400'],
];
export const GREEN_BUTTON_FILL: Array<[string, string]> = [
	['0', '#66D93D'],
	['0.55', '#248F1C'],
	['1', '#0D540F'],
];
export const HERO_FILL: Array<[string, string]> = [
	['0', '#450602'],
	['0.52', '#110101'],
	['1', '#420501'],
];
export const TAB_ACTIVE_FILL: Array<[string, string]> = [
	['0', '#970501'],
	['1', '#220802'],
];
export const UNLOCK_BUTTON_FILL: Array<[string, string]> = TAB_ACTIVE_FILL;
export const PROGRESS_TRACK_FILL: Array<[string, string]> = [
	['0', '#330B05'],
	['1', '#0F0402'],
];
/** gold/50 -> gold/150 -> gold/500 -> gold/700: the file's gold ramp, used for every gold fill. */
export const GOLD_RAMP: Array<[string, string]> = [
	['0', '#FFF5B9'],
	['0.1543', '#FFEE82'],
	['0.5493', '#F4AE2B'],
	['1', '#D18003'],
];
export const NODE_GOLD_FILL = GOLD_RAMP;
/** The hero title's own ramp - warmer, and it bottoms out in burnt orange rather than gold. */
export const HERO_TITLE_RAMP: Array<[string, string]> = [
	['0', '#FFFAC7'],
	['0.48', '#FFD14F'],
	['1', '#C25C09'],
];

const glow = (color: string, opacity: number, radius: number) =>
	({
		shadowColor: color,
		shadowOffset: { width: 0, height: 0 },
		shadowOpacity: opacity,
		shadowRadius: radius,
		elevation: Math.round(radius * 0.8),
	}) as const;

// The bloom is the file's var(--glow-bloom-70): #FF7200 at 70%.
export const eventGlow = {
	// Figma: drop-shadow 0 0 2.5 rgba(255,178,26,0.55) on every card.
	panel: glow('#FFB21A', 0.55, 2.5),
	// drop-shadow 0 0 5 rgba(255,114,0,0.7) - var(--glow-bloom-70).
	bloom: glow('#FF7200', 0.7, 5),
	// drop-shadow 0 0 5 rgba(255,158,20,0.5) on the unlock button.
	passButton: glow('#FF9E14', 0.5, 5),
	green: glow('#40FF33', 0.18, 2),
} as const;

export const groupThousands = (value: number) =>
	String(Math.round(value)).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
